/**
 * Binding entry point: the page-wide `computeScheduler`, connected to the Rust core as soon as
 * the WebAssembly package loads. Read `crates/compute-scheduler` (Rustdoc) for the model,
 * policies, constants and guarantees; `docs/developer/browser-compute.md` is the short guide.
 *
 * One shared instance, because the scheduler's value comes from seeing all background work at
 * once (a background job must know an interactive job is queued). Features import
 * `computeScheduler` and either `register` a Worker adapter or `schedule` a job.
 *
 * Loading starts when this module is first imported (the energy-model hook imports it at app
 * start). Responsiveness observers (`window.__fluxPerf`) are installed at the same time when
 * enabled. A test may set `window.__fluxComputeConfig = { backgroundStartDelayMs, inputQuietMs }`
 * before the app loads; the values are passed to the Rust core, which validates them. Only
 * same-origin script can set it, so it opens no capability that script did not already have.
 *
 * @module apps/web/src/lib/compute
 */

import { setYieldPolicy } from './mainThread';
import { installResponsivenessObserver } from './perf';
import { ComputeScheduler, type ComputeOverrides } from './scheduler';
import { loadComputeModule, type ComputeModule, type PoolPolicyHandle } from './wasm';

export { adaptWorkerClient, type WorkerClientAdapterSpec } from './adapters';
export { runChunked, yieldToMain, LANE_PRIORITY, type ChunkOptions } from './mainThread';
export { installResponsivenessObserver, responsivenessEnabled, type FluxPerf } from './perf';
export { ComputeScheduler, type ComputeOverrides } from './scheduler';
export { WarmWorkerPool, type WorkerPoolOptions } from './workerPool';
export { loadComputeModule, type ComputeModule } from './wasm';
export * from './types';

/** Test overrides read from `window.__fluxComputeConfig` (numbers only; Rust validates them). */
function overrides(): ComputeOverrides {
  const raw = (globalThis as { __fluxComputeConfig?: Record<string, unknown> }).__fluxComputeConfig;
  const number = (value: unknown) => (typeof value === 'number' ? value : undefined);
  return {
    backgroundStartDelayMs: number(raw?.backgroundStartDelayMs),
    inputQuietMs: number(raw?.inputQuietMs),
  };
}

/** The page-wide scheduler. */
export const computeScheduler = new ComputeScheduler();

// Outside a browser page (unit tests, server rendering) there is nothing to load: decide
// synchronously so jobs start immediately instead of waiting for a load that cannot happen.
if (typeof location === 'undefined' || typeof WebAssembly === 'undefined')
  computeScheduler.fallBack();

/** The loaded Rust package, once available (pool policies are created from it). */
let computeModule: ComputeModule | undefined;

/** A Rust pool policy for a new `WarmWorkerPool`, or undefined before/without the package. */
export function createPoolPolicy(): PoolPolicyHandle | undefined {
  return computeModule ? new computeModule.PoolPolicy() : undefined;
}

/** Resolves once the scheduler is attached to Rust (`ready`) or fell back (`passthrough`). */
export const computeReady: Promise<'ready' | 'passthrough'> = loadComputeModule().then(module => {
  if (!module) {
    computeScheduler.fallBack();
    return 'passthrough';
  }
  computeModule = module;
  computeScheduler.attach(module, overrides());
  setYieldPolicy(elapsed => computeScheduler.shouldYield(elapsed));
  installResponsivenessObserver(module, computeScheduler.rustCore);
  return 'ready';
});
